/**
 * Módulo: Calculadora de 1RM (cliente)
 * =============================================================================
 * Fórmula: Brzycki (estándar en gym).
 * Validación: errores solo tras blur (no al cargar).
 */

import { initFormFieldValidation } from '../../../src/js/general/form-field-validation.js';

/** Tabla estándar %1RM → reps (referencia fuerza) */
const PERCENTAGE_CHART = [
  { pct: 100, reps: 1 },
  { pct: 95, reps: 2 },
  { pct: 90, reps: 4 },
  { pct: 85, reps: 6 },
  { pct: 80, reps: 8 },
  { pct: 75, reps: 10 },
  { pct: 70, reps: 12 },
  { pct: 65, reps: 16 },
  { pct: 60, reps: 20 },
  { pct: 55, reps: 24 },
  { pct: 50, reps: 30 },
];

const LB_TO_KG = 0.45359237;
const KG_TO_LB = 1 / LB_TO_KG;

const SELECTORS = {
  form: '#one-rep-max-form',
  units: '#orm-units',
  weight: '#orm-weight',
  reps: '#orm-reps',
  headline: '#orm-result-headline',
  placeholder: '#orm-result-placeholder',
  value: '#orm-result-value',
  unit: '#orm-result-unit',
  series: '#orm-result-series',
  chart: '#orm-chart',
  chartBody: '#orm-chart-body',
};

/**
 * Brzycki: 1RM = peso × (36 / (37 − reps))
 * @param {number} weight
 * @param {number} reps
 * @returns {number|null}
 */
function estimateOneRepMax(weight, reps) {
  if (!Number.isFinite(weight) || weight <= 0 || !Number.isFinite(reps) || reps < 1) {
    return null;
  }

  if (reps === 1) {
    return weight;
  }

  if (reps >= 37) {
    return null;
  }

  return weight * (36 / (37 - reps));
}

/**
 * Redondeo tabla %1RM: fracción ≥ 0.6 → entero superior; ≤ 0.5 → entero inferior.
 * @param {number} value
 */
function roundChartWeight(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }

  const normalized = Math.round(value * 1000) / 1000;
  const whole = Math.trunc(normalized);
  const frac = Math.abs(normalized - whole);

  if (frac >= 0.6) {
    return normalized >= 0 ? whole + 1 : whole - 1;
  }

  return whole;
}

/**
 * @param {number} value
 * @param {'lb'|'kg'} unit
 */
function formatWeight(value, unit) {
  if (unit === 'kg') {
    return Math.round(value * 10) / 10;
  }
  return Math.round(value);
}

/**
 * @param {number} oneRm
 * @param {'lb'|'kg'} unit
 */
function buildChartRows(oneRm, unit) {
  return PERCENTAGE_CHART.map(({ pct, reps }) => ({
    pct,
    weight: roundChartWeight((oneRm * pct) / 100),
    reps,
    unit,
  }));
}

/**
 * @param {HTMLFormElement} form
 */
function readForm(form) {
  const fd = new FormData(form);
  return {
    units: fd.get('units') || 'lb',
    weight: fd.get('weight'),
    reps: fd.get('reps'),
  };
}

function calculate(inputs) {
  const weight = parseFloat(String(inputs.weight).replace(',', '.'));
  const reps = parseInt(inputs.reps, 10);
  const units = inputs.units === 'kg' ? 'kg' : 'lb';

  if (Number.isNaN(weight) || Number.isNaN(reps) || weight <= 0 || reps < 1) {
    return { valid: false };
  }

  const oneRm = estimateOneRepMax(weight, reps);
  if (oneRm === null || oneRm <= 0) {
    return { valid: false };
  }

  return {
    valid: true,
    oneRm: formatWeight(oneRm, units),
    unit: units,
    chart: buildChartRows(oneRm, units),
    inputWeight: weight,
    inputReps: reps,
  };
}

function clearResults(root) {
  const headline = root.querySelector(SELECTORS.headline);
  const placeholder = root.querySelector(SELECTORS.placeholder);
  const series = root.querySelector(SELECTORS.series);
  const chart = root.querySelector(SELECTORS.chart);
  const chartBody = root.querySelector(SELECTORS.chartBody);

  if (headline) headline.hidden = true;
  if (placeholder) placeholder.hidden = false;
  if (series) {
    series.hidden = true;
    series.textContent = '';
  }
  if (chart) chart.hidden = true;
  if (chartBody) chartBody.replaceChildren();
}

function renderChart(root, rows) {
  const chartBody = root.querySelector(SELECTORS.chartBody);
  const chart = root.querySelector(SELECTORS.chart);
  if (!chartBody || !chart) {
    return;
  }

  chartBody.replaceChildren();

  rows.forEach((row) => {
    const rowEl = document.createElement('div');
    rowEl.className = 'orm-chart__row';
    rowEl.setAttribute('role', 'row');

    const pctCell = document.createElement('div');
    pctCell.className = 'orm-chart__cell';
    pctCell.setAttribute('role', 'cell');
    pctCell.dataset.label = 'Porcentaje de 1RM';
    pctCell.textContent = `${row.pct}%`;

    const weightCell = document.createElement('div');
    weightCell.className = 'orm-chart__cell';
    weightCell.setAttribute('role', 'cell');
    weightCell.dataset.label = 'Peso';
    weightCell.textContent = `${row.weight} ${row.unit}`;

    const repsCell = document.createElement('div');
    repsCell.className = 'orm-chart__cell';
    repsCell.setAttribute('role', 'cell');
    repsCell.dataset.label = 'Repeticiones';
    repsCell.textContent = String(row.reps);

    rowEl.append(pctCell, weightCell, repsCell);
    chartBody.appendChild(rowEl);
  });

  chart.hidden = false;
}

function renderResults(root, data) {
  if (!data.valid) {
    clearResults(root);
    return;
  }

  const headline = root.querySelector(SELECTORS.headline);
  const placeholder = root.querySelector(SELECTORS.placeholder);
  const valueEl = root.querySelector(SELECTORS.value);
  const unitEl = root.querySelector(SELECTORS.unit);
  const seriesEl = root.querySelector(SELECTORS.series);

  if (placeholder) placeholder.hidden = true;
  if (headline) headline.hidden = false;
  if (valueEl) valueEl.textContent = String(data.oneRm);
  if (unitEl) unitEl.textContent = data.unit;
  if (seriesEl) {
    seriesEl.textContent = `Serie de referencia: ${data.inputWeight} ${data.unit} × ${data.inputReps} reps`;
    seriesEl.hidden = false;
  }

  renderChart(root, data.chart);

  document.dispatchEvent(
    new CustomEvent('titanfit:one-rep-max-calculated', {
      bubbles: true,
      detail: { results: data },
    })
  );
}

function convertWeightValue(value, fromUnit, toUnit) {
  if (!Number.isFinite(value) || fromUnit === toUnit) {
    return value;
  }
  return fromUnit === 'lb' ? value * LB_TO_KG : value * KG_TO_LB;
}

function initOneRepMax(root) {
  if (!root) {
    return;
  }

  const form = root.querySelector(SELECTORS.form);
  if (!form) {
    return;
  }

  const unitsSelect = form.querySelector(SELECTORS.units);
  const weightInput = form.querySelector(SELECTORS.weight);

  const fieldValidation = initFormFieldValidation(form);

  /** isValid() no pinta errores; validateAll() solo si el usuario ya interactuó */
  const run = () => {
    const inputs = readForm(form);
    const canCalculate = fieldValidation.isValid();
    const data = canCalculate ? calculate(inputs) : { valid: false };
    renderResults(root, data);
  };

  form.addEventListener('input', run);
  form.addEventListener('change', run);

  unitsSelect?.addEventListener('change', (ev) => {
    const nextUnit = ev.target.value === 'kg' ? 'kg' : 'lb';
    const prevUnit = nextUnit === 'kg' ? 'lb' : 'kg';

    if (weightInput?.value) {
      const converted = convertWeightValue(parseFloat(weightInput.value), prevUnit, nextUnit);
      if (Number.isFinite(converted)) {
        weightInput.value =
          nextUnit === 'kg' ? String(Math.round(converted * 10) / 10) : String(Math.round(converted));
      }
    }

    run();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-one-rep-max]').forEach((el) => initOneRepMax(el));
});
